import React, { useState, useEffect, useMemo } from 'react';
import { BRAND } from '../../../config/brand';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button, 
  ButtonGroup,
  CircularProgress, 
  Alert,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import AddIcon from '@mui/icons-material/Add';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DownloadIcon from '@mui/icons-material/Download'; // İndirme ikonu
import AppleIcon from '@mui/icons-material/Apple'; // Apple ikonu
import WindowIcon from '@mui/icons-material/Window'; // Windows ikonu (Microsoft logosu için alternatif)
import MoreVertIcon from '@mui/icons-material/MoreVert'; // 3 nokta menü ikonu
import AssessmentIcon from '@mui/icons-material/Assessment'; // Gün sonu ikonu
import HistoryIcon from '@mui/icons-material/History'; // Gün sonları listesi ikonu
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; // Raporlar ikonu
import SettingsIcon from '@mui/icons-material/Settings'; // Ayarlar ikonu
import DeleteIcon from '@mui/icons-material/Delete'; // Sil ikonu
import EditIcon from '@mui/icons-material/Edit'; // Düzenle ikonu
import CampaignIcon from '@mui/icons-material/Campaign'; // ✨ Duyuru ikonu
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddQuickSaleScreenModal from './components/modals/AddQuickSaleScreenModal';
import useSalesScreensPageAgentBridge from '../hooks/useSalesScreensPageAgentBridge';
import QuickSaleScreenSettingsModal from './components/modals/QuickSaleScreenSettingsModal';
import PykScreenSettingsModal from './components/PykScreenSettingsModal'; // PYK Ekranı Ayarları için import
import AddPykScreenModal from './components/modals/AddPykScreenModal';
import AnnouncementsManagementModal from './components/modals/AnnouncementsManagementModal'; // ✨ Duyuru sistemi modali
import salesService from '../../../services/salesService';
import { useAuth } from '../../../contexts/AuthContext';
import RecordHistoryModal from '../../common/RecordHistoryModal';
import GuideButton from '../../common/guide/GuideButton';
import BranchSearchFilterBar, { UNASSIGNED_BRANCH_ID } from '../../common/BranchSearchFilterBar';
import { getPersonnel } from '../../../services/migration/personnelDataService';

// Hızlı Satış Ekranı Veri Tipi
interface QuickSaleScreenData { // Adını değiştirdim, QuickSaleScreen.tsx ile çakışmasın diye
  id: string;
  name: string;
  personnelName?: string;
  branchId?: string | null;
  branchName?: string;
  source: 'firebase' | 'sql'; // Veri kaynağı
  screenType?: string; // 'Standalone' | 'Restaurant'
}

// PYK Ekranı Veri Tipi
interface PykScreenData {
  id: string;
  name: string;
  branchId?: string | null;
  branchName?: string;
  source?: 'firebase' | 'sql'; // ✅ İleride SQL migration için hazırlık
}

// İşlem Geçmişi modalı alan adı Türkçeleştirmesi (backend QuickSaleScreen property adları).
const QUICK_SALE_HISTORY_FIELD_LABELS: Record<string, string> = {
  Name: 'Ekran Adı',
  BranchId: 'Şube',
  ScreenType: 'Ekran Tipi',
  RestaurantScreenId: 'Restoran Ekranı',
  PackageScreenId: 'Paket Ekranı',
  AssignedPersonnelJson: 'Atanan Personeller',
  PaymentOptionsJson: 'Ödeme Yöntemleri',
  StockVisibilityJson: 'Depo/Stok Ayarları',
  PrintingSettingsJson: 'Yazdırma Ayarları',
  PersonnelPermissionsJson: 'Personel Yetkileri',
  PrepaidSystemEnabled: 'Ön Ödemeli Sistem',
  ShowProductImages: 'Ürün Resimleri',
  UseFiscalDeviceForCash: 'Nakit İçin Yazarkasa',
  SelectedFiscalDevice: 'Yazarkasa Markası',
  WarnIfFiscalNotConnected: 'Yazarkasa Bağlı Değil Uyarısı',
  UsePavoPayForOtherPayments: 'Diğer Ödemelerde PavoPay',
  UseExternalPrinterForPavo: 'PavoPay Harici Yazıcı',
  UseRestP2PForPayments: 'Rest-P2P Ödeme',
  PavoRestP2PDeviceScreenCode: 'Rest-P2P Cihaz Kodu',
  AllowAccountCreationOnSale: 'Satışta Cari Oluşturma',
  UseKitchenScreen: 'Mutfak Ekranı',
  ShowScreenLayoutButton: 'Görünüm Ayarları Butonu',
  SelectedQRMenuId: 'Seçili QR Menü',
  UseCashierSystem: 'Kasiyer Sistemi',
  ShowCashChangeModal: 'Para Üstü Modalı',
  LocalServiceUrl: 'Yerel Servis URL',
  AutoLockEnabled: 'Oto Kilitleme',
  AutoLockTimeoutSeconds: 'Oto Kilitleme Süresi (sn)',
  PredefinedDeleteReasonsJson: 'Hazır Silme Nedenleri',
  IsActive: 'Aktif mi',
  IsDeleted: 'Silindi mi',
};

// Accounts.tsx'den alınan stil bileşenleri
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    height: 'auto',
    overflowY: 'visible',
  },
  [theme.breakpoints.up('md')]: {
    overflowY: 'auto',
    height: 'calc(100vh - 64px)',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));


const QuickSalePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Mobil kontrol
  const { getBranchName, userDetails, branches } = useAuth(); // ✅ AuthContext'ten getBranchName, branches ve userDetails alınıyor
  
  // ✅ companyId artık AuthContext'ten alınıyor
  const companyId = userDetails?.companyId || null;
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddPykModalOpen, setIsAddPykModalOpen] = useState(false);
  const [quickSaleScreens, setQuickSaleScreens] = useState<QuickSaleScreenData[]>([]);
  const [pykScreens, setPykScreens] = useState<PykScreenData[]>([]); // YENİ: PYK ekranları için
  const [loadingScreens, setLoadingScreens] = useState(true);
  const [loadingPykScreens, setLoadingPykScreens] = useState(false); // ✅ Başlangıçta false - PYK henüz SQL'e taşınmadı
  const [screenError, setScreenError] = useState<string | null>(null);
  const [pykScreenError, setPykScreenError] = useState<string | null>(null); // YENİ: PYK ekranları hata state
  const [screenToDelete, setScreenToDelete] = useState<QuickSaleScreenData | null>(null);
  const [pykScreenToDelete, setPykScreenToDelete] = useState<PykScreenData | null>(null); // YENİ: Silinecek PYK ekranı
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPykDeleteModalOpen, setIsPykDeleteModalOpen] = useState(false); // YENİ: PYK silme modal state
  const [deletingScreen, setDeletingScreen] = useState(false);
  const [deletingPykScreen, setDeletingPykScreen] = useState(false); // YENİ: PYK ekranı siliniyor state

  // Şube + ekran adı filtresi (hem hızlı satış hem PYK listesi için ortak).
  const [filterBranchId, setFilterBranchId] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');

  const matchesFilter = (screen: { name: string; branchId?: string | null }) => {
    if (filterBranchId === UNASSIGNED_BRANCH_ID) {
      if (screen.branchId) return false;
    } else if (filterBranchId && screen.branchId !== filterBranchId) {
      return false;
    }
    const term = filterSearch.trim().toLocaleLowerCase('tr');
    if (!term) return true;
    return (screen.name || '').toLocaleLowerCase('tr').includes(term);
  };

  const filteredQuickSaleScreens = useMemo(
    () => quickSaleScreens.filter(matchesFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quickSaleScreens, filterBranchId, filterSearch]
  );
  const filteredPykScreens = useMemo(
    () => pykScreens.filter(matchesFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pykScreens, filterBranchId, filterSearch]
  );
  const isFilterActive = Boolean(filterBranchId) || filterSearch.trim().length > 0;

  // Ayarlar Modalı için State'ler
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedScreenForSettings, setSelectedScreenForSettings] = useState<QuickSaleScreenData | null>(null);
  
  // YENİ: PYK Ayarlar Modalı için State'ler
  const [isPykSettingsModalOpen, setIsPykSettingsModalOpen] = useState(false);
  const [selectedPykScreenForSettings, setSelectedPykScreenForSettings] = useState<PykScreenData | null>(null);

  const [reportNoticeOpen, setReportNoticeOpen] = useState(false);
  
  // 3 nokta menüsü
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Satır 3 nokta menüsü (Hızlı Satış)
  const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedScreenForMenu, setSelectedScreenForMenu] = useState<QuickSaleScreenData | null>(null);
  
  // Satır 3 nokta menüsü (PYK)
  const [pykRowMenuAnchor, setPykRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedPykScreenForMenu, setSelectedPykScreenForMenu] = useState<PykScreenData | null>(null);
  
  // Düzenleme modalı için state'ler
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [screenToEdit, setScreenToEdit] = useState<QuickSaleScreenData | null>(null);

  // ✨ Duyuru sistemi modali state'leri
  const [generalAnnouncementsOpen, setGeneralAnnouncementsOpen] = useState(false);
  const [perScreenAnnouncements, setPerScreenAnnouncements] = useState<{
    screenId: number;
    screenName: string;
    branchId?: string | null;
  } | null>(null);

  // ✅ companyId artık AuthContext'ten alınıyor, Firebase çağrısı kaldırıldı

  // Hızlı satış ekranlarını yükle (SQL'den) - SADECE İLK YÜKLEME
  useEffect(() => {
    if (!companyId) {
      // companyId henüz yüklenmediyse bekle
      return;
    }

    const fetchAllScreens = async () => {
      setLoadingScreens(true);
      setScreenError(null);
      
      try {
        const sqlScreens = await salesService.getQuickSaleScreens();
        
        // Sadece aktif ekranları filtrele (IsActive = true veya IsActive undefined)
        const activeScreens = sqlScreens.filter(screen => screen.IsActive !== false);
        
        // ✅ Şube adlarını AuthContext'ten al
        // KRITIK: branches listesi henuz yuklenmemis olabilir (sayfa yenilendiginde
        // companyId cache'ten erken gelir, branches API/cache'ten async yuklenir).
        // Bu durumda getBranchName "Şubeye Ait Değil" doner ve state'e kalici yazilir;
        // asagidaki branches-effect daha sonra branches gelince yeniden hesaplar.
        // Burada branches bos iken `undefined` birakiyoruz ki effect duzgun calissin.
        const branchesReady = branches.length > 0;
        const sqlList: QuickSaleScreenData[] = activeScreens.map(screen => ({
          id: screen.Id.toString(),
          name: screen.Name,
          personnelName: 'SQL Ekranı',
          branchId: screen.BranchId || null,
          branchName: branchesReady && screen.BranchId ? getBranchName(screen.BranchId) : undefined,
          source: 'sql' as const,
          screenType: screen.ScreenType || 'Standalone'
        }));
        
        const standaloneScreens = sqlList.filter(s => !s.screenType || s.screenType === 'Standalone');
        
        setQuickSaleScreens(standaloneScreens.sort((a, b) => a.name.localeCompare(b.name)));

        console.log(`✅ ${standaloneScreens.length} aktif hızlı satış ekranı SQL'den yüklendi`);
      } catch (error) {
        console.error("❌ SQL'den ekranlar yüklenirken hata:", error);
        setScreenError('Hızlı satış ekranları yüklenirken bir hata oluştu.');
        setQuickSaleScreens([]);
      } finally {
        setLoadingScreens(false);
      }
    };

    fetchAllScreens();
    
    // ❌ Polling kaldırıldı - manuel yenileme için refreshScreenList kullanılacak
  }, [companyId]); // ✅ Sadece companyId değiştiğinde çalışır, getBranchName kaldırıldı

  // PYK ekranlarını SQL'den yükle
  const fetchPykScreens = async () => {
    if (!companyId) return;
    setLoadingPykScreens(true);
    setPykScreenError(null);
    try {
      const screens = await salesService.getPykScreens();
      const activeScreens = screens.filter(s => s.IsActive !== false);
      // KRITIK: branches henuz hazir degilse branchName'i undefined birak;
      // asagidaki branches-effect sonradan dogru ismi yazacak.
      const branchesReady = branches.length > 0;
      const pykList: PykScreenData[] = activeScreens.map(s => ({
        id: s.Id.toString(),
        name: s.Name,
        branchId: s.BranchId || null,
        branchName: branchesReady && s.BranchId ? getBranchName(s.BranchId) : undefined,
        source: 'sql' as const,
      }));
      setPykScreens(pykList.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("PYK ekranları yüklenirken hata:", error);
      setPykScreenError('PYK ekranları yüklenirken bir hata oluştu.');
      setPykScreens([]);
    } finally {
      setLoadingPykScreens(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchPykScreens();
    }
  }, [companyId]);

  // 🔧 Sayfa yenilendiginde branches AuthContext'ten async yuklenir;
  // companyId genelde once gelir ve fetch* fonksiyonlari `branches` bos iken
  // calisir, branchName "Şubeye Ait Değil" olarak state'e yazilirdi (truthy
  // string oldugu icin UI fallback'i devre disi kalip kalici yanlis metin
  // gozukurdu). Bu effect branches yuklendiginde mevcut iki listenin
  // branchName'lerini yeniden hesaplar — ekran yenilemeye gerek kalmadan
  // sube isimleri otomatik olarak dogru gozukur.
  useEffect(() => {
    if (branches.length === 0) return;
    setQuickSaleScreens(prev => {
      // Yalnizca branchName'i undefined olan kayitlari guncelle (gereksiz
      // re-render onlemek icin; degisiklik yoksa ayni referansi don).
      const needsUpdate = prev.some(s => s.branchId && !s.branchName);
      if (!needsUpdate) return prev;
      return prev.map(s => ({
        ...s,
        branchName: s.branchId ? getBranchName(s.branchId) : undefined,
      }));
    });
    setPykScreens(prev => {
      const needsUpdate = prev.some(s => s.branchId && !s.branchName);
      if (!needsUpdate) return prev;
      return prev.map(s => ({
        ...s,
        branchName: s.branchId ? getBranchName(s.branchId) : undefined,
      }));
    });
  }, [branches, getBranchName]);

  const handleOpenDeleteModal = (screen: QuickSaleScreenData) => {
    setScreenToDelete(screen);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setScreenToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteScreen = async () => {
    if (!screenToDelete || !companyId) {
      setScreenError("Silinecek ekran bilgisi veya şirket ID bulunamadı.");
      return;
    }
    setDeletingScreen(true);
    setScreenError(null);
    const screenIdToDelete = screenToDelete.id;

    try {
      // ✅ SADECE SQL - Artık tüm ekranlar SQL'de
      const screenIdAsNumber = parseInt(screenIdToDelete, 10);
      if (isNaN(screenIdAsNumber)) {
        throw new Error(`Geçersiz ekran ID'si: ${screenIdToDelete}`);
      }
      
      await salesService.deleteQuickSaleScreen(screenIdAsNumber);
      console.log(`✅ SQL Hızlı satış ekranı silindi (ID: ${screenIdAsNumber})`);
      
      setQuickSaleScreens(prevScreens => prevScreens.filter(s => s.id !== screenIdToDelete));
      
      handleCloseDeleteModal();
    } catch (error) {
      console.error("❌ Hızlı satış ekranı silinirken hata:", error);
      setScreenError(`Ekran silinirken bir hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    } finally {
      setDeletingScreen(false);
    }
  };

  const handleOpenSettingsModal = (screen: QuickSaleScreenData) => {
    setSelectedScreenForSettings(screen);
    setIsSettingsModalOpen(true);
  };

  const handleCloseSettingsModal = () => {
    setSelectedScreenForSettings(null);
    setIsSettingsModalOpen(false);
    // Ayarlar kaydedildikten sonra listeyi yenilemek gerekebilir, 
    // ancak modal kendi içinde kaydetme ve güncelleme yapacak.
    // Şimdilik ana listeyi direkt yenilemeye gerek yok gibi.
  };

  // YENİ: PYK ekranı ayarlar modalını açma fonksiyonu
  const handleOpenPykSettingsModal = (screen: PykScreenData) => {
    setSelectedPykScreenForSettings(screen);
    setIsPykSettingsModalOpen(true);
  };
  
  // YENİ: PYK ekranı ayarlar modalını kapatma fonksiyonu
  const handleClosePykSettingsModal = () => {
    setSelectedPykScreenForSettings(null);
    setIsPykSettingsModalOpen(false);
  };

  // YENİ: PYK ekranı silme modalını açma fonksiyonu
  const handleOpenPykDeleteModal = (screen: PykScreenData) => {
    setPykScreenToDelete(screen);
    setIsPykDeleteModalOpen(true);
  };

  // YENİ: PYK ekranı silme modalını kapatma fonksiyonu
  const handleClosePykDeleteModal = () => {
    setPykScreenToDelete(null);
    setIsPykDeleteModalOpen(false);
  };

  // YENİ: PYK ekranı silme fonksiyonu (SQL only)
  const handleDeletePykScreen = async () => {
    if (!pykScreenToDelete || !companyId) {
      setPykScreenError("Silinecek PYK ekranı bilgisi veya şirket ID bulunamadı.");
      return;
    }
    setDeletingPykScreen(true);
    setPykScreenError(null);
    const screenIdToDelete = pykScreenToDelete.id;

    try {
      // ✅ SADECE SQL - Firebase desteği kaldırıldı
      const screenIdAsNumber = parseInt(screenIdToDelete, 10);
      if (isNaN(screenIdAsNumber)) {
        throw new Error(`Geçersiz PYK ekran ID'si: ${screenIdToDelete}`);
      }
      
      await salesService.deletePykScreen(screenIdAsNumber);
      console.log(`✅ SQL PYK ekranı silindi (ID: ${screenIdAsNumber})`);
      
      // Liste güncellenir
      setPykScreens(prevScreens => prevScreens.filter(s => s.id !== screenIdToDelete));
      
      handleClosePykDeleteModal();
    } catch (error) {
      console.error("❌ PYK ekranı silinirken hata:", error);
      setPykScreenError(`PYK ekranı silinirken bir hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    } finally {
      setDeletingPykScreen(false);
    }
  };

  const handleReportClick = (screenId: string, isQuickSale: boolean = true) => {
    const screenType = isQuickSale ? 'quicksale' : 'pyk';
    navigate(`/hizli-satis/raporlar/${screenType}/${screenId}`);
  };

  const handleCloseReportNotice = () => {
    setReportNoticeOpen(false);
  };

  // Satır menüsü handler'ları (Hızlı Satış)
  const handleOpenRowMenu = (event: React.MouseEvent<HTMLElement>, screen: QuickSaleScreenData) => {
    setRowMenuAnchor(event.currentTarget);
    setSelectedScreenForMenu(screen);
  };

  const handleCloseRowMenu = () => {
    setRowMenuAnchor(null);
    setSelectedScreenForMenu(null);
  };

  // İşlem Geçmişi modalı — handleCloseRowMenu selectedScreenForMenu'yü sıfırladığı için
  // ekran bilgisi ayrı state'te saklanır (yalnız SQL kaynaklı ekranlar: entityId int).
  const [historyModalScreen, setHistoryModalScreen] = useState<{ id: number; label: string } | null>(null);

  // İşlem geçmişindeki JSON alanlarda (AssignedPersonnelJson vb.) uid yerine
  // personel adı gösterilsin diye uid → ad haritası; modal ilk açıldığında yüklenir.
  const [historyPersonnelLabels, setHistoryPersonnelLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!historyModalScreen || !companyId || Object.keys(historyPersonnelLabels).length > 0) return;
    let alive = true;
    getPersonnel(companyId, { includeDeleted: true })
      .then((list) => {
        if (!alive) return;
        const map: Record<string, string> = {};
        for (const p of list) {
          if (p.id) map[p.id] = p.name || p.email || p.id;
        }
        setHistoryPersonnelLabels(map);
      })
      .catch(() => {
        // İsim çözülemezse modal ham uid gösterir; işlevi engellemez.
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyModalScreen, companyId]);

  // Düzenleme modalı handler'ları
  const handleOpenEditModal = (screen: QuickSaleScreenData) => {
    setScreenToEdit(screen);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setScreenToEdit(null);
    setIsEditModalOpen(false);
  };

  // Ekran listesini yenile (düzenleme/ekleme sonrası)
  // AI ajan köprüsü: sayfa açıkken ajan ekran listeleyebilir, ayar modalını
  // açabilir ve yeni ekran sonrası listeyi yenileyebilir (uiBridge üzerinden).
  useSalesScreensPageAgentBridge('quickSale', {
    screens: quickSaleScreens,
    openSettings: (screen) => {
      const full = quickSaleScreens.find((s) => s.id === screen.id);
      if (full) handleOpenSettingsModal(full);
    },
    refreshScreens: () => refreshScreenList(),
  });

  const refreshScreenList = async () => {
    if (!companyId) return;
    
    try {
      const sqlScreens = await salesService.getQuickSaleScreens();
      const activeScreens = sqlScreens.filter(screen => screen.IsActive !== false);
      
      const branchesReady = branches.length > 0;
      const sqlList: QuickSaleScreenData[] = activeScreens.map(screen => ({
        id: screen.Id.toString(),
        name: screen.Name,
        personnelName: 'SQL Ekranı',
        branchId: screen.BranchId || null,
        branchName: branchesReady && screen.BranchId ? getBranchName(screen.BranchId) : undefined,
        source: 'sql' as const,
        screenType: screen.ScreenType || 'Standalone'
      }));
      
      const standaloneScreens = sqlList.filter(s => !s.screenType || s.screenType === 'Standalone');
      
      setQuickSaleScreens(standaloneScreens.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Ekran listesi yenilenirken hata:", error);
    }
  };

  // Satır menüsü handler'ları (PYK)
  const handleOpenPykRowMenu = (event: React.MouseEvent<HTMLElement>, screen: PykScreenData) => {
    setPykRowMenuAnchor(event.currentTarget);
    setSelectedPykScreenForMenu(screen);
  };

  const handleClosePykRowMenu = () => {
    setPykRowMenuAnchor(null);
    setSelectedPykScreenForMenu(null);
  };

  return (
    <ScrollableContent>
      <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <FlashOnIcon sx={{ fontSize: { xs: 24, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('quickSalePage.title')}
          </Typography>
          <GuideButton guideKey="sales.quickSalePage" sx={{ ml: 0.5 }} />
        </Box>
        {/* Desktop Butonlar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
          <ButtonGroup>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsAddModalOpen(true)}
            >
              {t('quickSalePage.actions.newSalesScreen')}
            </Button>
            <Button
              variant="outlined"
              onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              <MoreVertIcon />
            </Button>
          </ButtonGroup>
          
          {/* Menü İçeriği */}
          <Menu
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={() => setMoreMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setIsAddPykModalOpen(true); setMoreMenuAnchor(null); }}>
              <ListItemIcon>
                <AddIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.addPykScreen')}</ListItemText>
            </MenuItem>
            <Divider />
            {/* ✨ Genel Duyuru Sistemi */}
            <MenuItem onClick={() => { setGeneralAnnouncementsOpen(true); setMoreMenuAnchor(null); }}>
              <ListItemIcon>
                <CampaignIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t('announcements.menu.general', 'Genel Duyuru Sistemi')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { navigate('/gun-sonu'); setMoreMenuAnchor(null); }}>
              <ListItemIcon>
                <AssessmentIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.endOfDayReport')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { navigate('/gun-sonlari'); setMoreMenuAnchor(null); }}>
              <ListItemIcon>
                <HistoryIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.endOfDayList')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { window.open(BRAND.apps.windowsApp, '_blank'); setMoreMenuAnchor(null); }}>
              <ListItemIcon>
                <WindowIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.printServiceWindows')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { window.open('https://drive.google.com/file/d/1ZfVoVAdzq9D2Fw5qaegJyUDI4SBOzE71/view?usp=drive_link', '_blank'); setMoreMenuAnchor(null); }}>
              <ListItemIcon>
                <AppleIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.printServiceMac')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
        
        {/* Mobil Butonlar */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, width: '100%' }}>
          <Button 
            fullWidth
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsAddModalOpen(true)}
            size="small"
          >
            {t('quickSalePage.actions.newSalesScreen')}
          </Button>
          <Button
            variant="outlined"
            onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
            size="small"
            sx={{ minWidth: 'auto', px: 1 }}
          >
            <MoreVertIcon />
          </Button>
        </Box>
      </HeaderContainer>

      {screenError && <Alert severity="error" sx={{ mb: 2 }}>{screenError}</Alert>}

      {/* Filtre iki listeyi (hızlı satış + PYK) birlikte süzer. */}
      {!loadingScreens && quickSaleScreens.length + pykScreens.length > 0 && (
        <BranchSearchFilterBar
          branches={branches}
          branchId={filterBranchId}
          onBranchChange={setFilterBranchId}
          search={filterSearch}
          onSearchChange={setFilterSearch}
          searchLabel={t('quickSalePage.table.screenName')}
          shownCount={filteredQuickSaleScreens.length + filteredPykScreens.length}
          totalCount={quickSaleScreens.length + pykScreens.length}
        />
      )}

      {/* Hızlı Satış Ekranları */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
        <PointOfSaleIcon sx={{ mr: 1 }} /> {t('quickSalePage.quickSaleScreens.title')}
      </Typography>
      
      {loadingScreens ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : filteredQuickSaleScreens.length === 0 && !screenError ? (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <Typography color="text.secondary">
            {isFilterActive && quickSaleScreens.length > 0
              ? t('common2:listFilter.noResults')
              : t('quickSalePage.quickSaleScreens.emptyState')}
          </Typography>
        </Paper>
      ) : !screenError ? (
        <>
          {/* 📱 Mobile Card View (xs, sm) */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
            {filteredQuickSaleScreens.map((screen) => (
              <Paper 
                key={screen.id}
                elevation={2} 
                sx={{ 
                  p: 2, 
                  mb: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { 
                    boxShadow: 4,
                    borderColor: 'primary.main'
                  }
                }}
              >
                {/* Ekran Adı */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <PointOfSaleIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    {screen.name}
                  </Typography>
                </Box>

                {/* Şube Bilgisi */}
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2" color="text.secondary">{t('quickSalePage.table.branch')}:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {screen.branchName || t('quickSalePage.table.branchUnassigned')}
                  </Typography>
                </Box>

                {/* İşlem Butonları */}
                <Box display="flex" gap={1} alignItems="center">
                  <Button 
                    fullWidth
                    size="small" 
                    variant="outlined"
                    onClick={() => navigate(`/hizli-satis/ekran/${screen.id}`)}
                  >
                    {t('quickSalePage.actions.goToScreen')}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenRowMenu(e, screen)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>

          {/* 🖥️ Desktop Table View (md+) */}
          <Paper elevation={3} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '40%' }}>{t('quickSalePage.table.screenName')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '20%' }}>{t('quickSalePage.table.branch')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', textAlign: 'right', width: '40%' }}>{t('quickSalePage.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredQuickSaleScreens.map((screen) => (
                    <TableRow 
                      key={screen.id}
                      hover
                    >
                      <TableCell onClick={() => navigate(`/hizli-satis/ekran/${screen.id}`)} sx={{cursor: 'pointer'}}>{screen.name}</TableCell>
                      <TableCell>{screen.branchName || t('quickSalePage.table.branchUnassigned')}</TableCell>
                      <TableCell align="right" sx={{ p: '6px' }}> 
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => navigate(`/hizli-satis/ekran/${screen.id}`)}
                          sx={{ mr: 1 }}
                        >
                          {t('quickSalePage.actions.goToScreen')}
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenRowMenu(e, screen)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Hızlı Satış Satır Menüsü */}
          <Menu
            anchorEl={rowMenuAnchor}
            open={Boolean(rowMenuAnchor)}
            onClose={handleCloseRowMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => {
              if (selectedScreenForMenu) handleOpenEditModal(selectedScreenForMenu);
              handleCloseRowMenu();
            }}>
              <ListItemIcon>
                <EditIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.edit')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedScreenForMenu) handleReportClick(selectedScreenForMenu.id, true);
              handleCloseRowMenu();
            }}>
              <ListItemIcon>
                <ReceiptLongIcon fontSize="small" color="info" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.reports')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedScreenForMenu) handleOpenSettingsModal(selectedScreenForMenu);
              handleCloseRowMenu();
            }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.settings')}</ListItemText>
            </MenuItem>
            {/* ✨ Bu Ekrana Özel Duyurular */}
            <MenuItem onClick={() => {
              if (selectedScreenForMenu) {
                const sid = parseInt(selectedScreenForMenu.id, 10);
                if (!isNaN(sid)) {
                  setPerScreenAnnouncements({
                    screenId: sid,
                    screenName: selectedScreenForMenu.name,
                    branchId: selectedScreenForMenu.branchId,
                  });
                }
              }
              handleCloseRowMenu();
            }}>
              <ListItemIcon>
                <CampaignIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t('announcements.menu.perScreen', 'Bu Ekrana Özel Duyurular')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedScreenForMenu) {
                const sid = parseInt(selectedScreenForMenu.id, 10);
                if (!isNaN(sid)) {
                  setHistoryModalScreen({ id: sid, label: selectedScreenForMenu.name });
                }
              }
              handleCloseRowMenu();
            }}>
              <ListItemIcon>
                <HistoryIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText>{t('common:recordHistory.menuItem')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              if (selectedScreenForMenu) {
                setScreenToDelete(selectedScreenForMenu);
                setIsDeleteModalOpen(true);
              }
              handleCloseRowMenu();
            }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>{t('quickSalePage.actions.delete')}</ListItemText>
            </MenuItem>
          </Menu>

          {/* İşlem Geçmişi (Entity Audit Trail) — hızlı satış ekranı ayar değişiklikleri */}
          <RecordHistoryModal
            open={Boolean(historyModalScreen)}
            onClose={() => setHistoryModalScreen(null)}
            entityType="QuickSaleScreen"
            entityId={historyModalScreen?.id ?? null}
            recordLabel={historyModalScreen?.label}
            fieldLabels={QUICK_SALE_HISTORY_FIELD_LABELS}
            jsonKeyLabels={historyPersonnelLabels}
          />
        </>
      ) : null}

      {/* Restoran ve Paket ekranları kendi bölümlerinde yönetiliyor */}

      {/* YENİ: PYK Ekranları */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
        <PointOfSaleIcon sx={{ mr: 1 }} /> {t('quickSalePage.pykScreens.title')}
      </Typography>
      
      {pykScreenError && <Alert severity="error" sx={{ mb: 2 }}>{pykScreenError}</Alert>}
      
      {loadingPykScreens ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : filteredPykScreens.length === 0 && !pykScreenError ? (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <Typography color="text.secondary">
            {isFilterActive && pykScreens.length > 0
              ? t('common2:listFilter.noResults')
              : t('quickSalePage.pykScreens.emptyState')}
          </Typography>
        </Paper>
      ) : !pykScreenError ? (
        <>
          {/* 📱 Mobile Card View (xs, sm) */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
            {filteredPykScreens.map((screen) => (
              <Paper 
                key={screen.id}
                elevation={2} 
                sx={{ 
                  p: 2, 
                  mb: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'secondary.main',
                  bgcolor: 'secondary.50',
                  '&:hover': { 
                    boxShadow: 4,
                    borderColor: 'secondary.dark'
                  }
                }}
              >
                {/* Ekran Adı */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <PointOfSaleIcon sx={{ fontSize: 24, color: 'secondary.main' }} />
                  <Typography variant="h6" fontWeight="bold" color="secondary.dark">
                    {screen.name}
                  </Typography>
                </Box>

                {/* Şube Bilgisi */}
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2" color="text.secondary">{t('quickSalePage.table.branch')}:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {screen.branchName || t('quickSalePage.table.branchUnassigned')}
                  </Typography>
                </Box>

                {/* İşlem Butonları */}
                <Box display="flex" gap={1} alignItems="center">
                  <Button 
                    fullWidth
                    size="small" 
                    variant="outlined"
                    onClick={() => navigate(`/pyk-ekrani/${screen.id}`)}
                  >
                    {t('quickSalePage.actions.goToScreen')}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenPykRowMenu(e, screen)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>

          {/* 🖥️ Desktop Table View (md+) */}
          <Paper elevation={3} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '40%' }}>{t('quickSalePage.pykScreens.screenName')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '20%' }}>{t('quickSalePage.table.branch')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', textAlign: 'right', width: '40%' }}>{t('quickSalePage.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPykScreens.map((screen) => (
                    <TableRow 
                      key={screen.id}
                      hover
                    >
                      <TableCell onClick={() => navigate(`/pyk-ekrani/${screen.id}`)} sx={{cursor: 'pointer'}}>{screen.name}</TableCell>
                      <TableCell>{screen.branchName || t('quickSalePage.table.branchUnassigned')}</TableCell>
                      <TableCell align="right" sx={{ p: '6px' }}> 
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => navigate(`/pyk-ekrani/${screen.id}`)}
                          sx={{ mr: 1 }}
                        >
                          {t('quickSalePage.actions.goToScreen')}
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenPykRowMenu(e, screen)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* PYK Satır Menüsü */}
          <Menu
            anchorEl={pykRowMenuAnchor}
            open={Boolean(pykRowMenuAnchor)}
            onClose={handleClosePykRowMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => {
              if (selectedPykScreenForMenu) handleReportClick(selectedPykScreenForMenu.id, false);
              handleClosePykRowMenu();
            }}>
              <ListItemIcon>
                <ReceiptLongIcon fontSize="small" color="info" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.reports')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedPykScreenForMenu) handleOpenPykSettingsModal(selectedPykScreenForMenu);
              handleClosePykRowMenu();
            }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText>{t('quickSalePage.actions.settings')}</ListItemText>
            </MenuItem>
            {/* ✨ Bu Ekrana Özel Duyurular (PYK) */}
            <MenuItem onClick={() => {
              if (selectedPykScreenForMenu) {
                const sid = parseInt(selectedPykScreenForMenu.id, 10);
                if (!isNaN(sid)) {
                  setPerScreenAnnouncements({
                    screenId: sid,
                    screenName: selectedPykScreenForMenu.name,
                    branchId: selectedPykScreenForMenu.branchId,
                  });
                }
              }
              handleClosePykRowMenu();
            }}>
              <ListItemIcon>
                <CampaignIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t('announcements.menu.perScreen', 'Bu Ekrana Özel Duyurular')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              if (selectedPykScreenForMenu) handleOpenPykDeleteModal(selectedPykScreenForMenu);
              handleClosePykRowMenu();
            }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>{t('quickSalePage.actions.delete')}</ListItemText>
            </MenuItem>
          </Menu>
        </>
      ) : null}

      <AddQuickSaleScreenModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        companyId={companyId}
        onSaved={refreshScreenList}
      />

      {/* Düzenleme Modalı */}
      {screenToEdit && (
        <AddQuickSaleScreenModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          companyId={companyId}
          editMode={true}
          screenId={screenToEdit.id}
          screenName={screenToEdit.name}
          screenBranchId={screenToEdit.branchId}
          onSaved={refreshScreenList}
        />
      )}

      {/* PYK MODALI */}
      <AddPykScreenModal
        open={isAddPykModalOpen}
        onClose={() => setIsAddPykModalOpen(false)}
        companyId={companyId}
        onSaved={fetchPykScreens}
      />

      {/* Hızlı Satış Silme Onay Modalı */}
      <Dialog open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>{t('quickSalePage.deleteDialog.quickSaleTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('quickSalePage.deleteDialog.quickSaleDescription', { name: screenToDelete?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} disabled={deletingScreen}>{t('quickSalePage.common.cancel')}</Button>
          <Button onClick={handleDeleteScreen} color="error" variant="contained" disabled={deletingScreen}>
            {deletingScreen ? <CircularProgress size={24} /> : t('quickSalePage.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* YENİ: PYK Silme Onay Modalı */}
      <Dialog open={isPykDeleteModalOpen} onClose={handleClosePykDeleteModal}>
        <DialogTitle>{t('quickSalePage.deleteDialog.pykTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('quickSalePage.deleteDialog.pykDescription', { name: pykScreenToDelete?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePykDeleteModal} disabled={deletingPykScreen}>{t('quickSalePage.common.cancel')}</Button>
          <Button onClick={handleDeletePykScreen} color="error" variant="contained" disabled={deletingPykScreen}>
            {deletingPykScreen ? <CircularProgress size={24} /> : t('quickSalePage.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ayarlar Modalı */}
      {selectedScreenForSettings && (
          <QuickSaleScreenSettingsModal 
            open={isSettingsModalOpen}
            onClose={handleCloseSettingsModal}
            screenId={selectedScreenForSettings.id}
            companyId={companyId}
            branchId={selectedScreenForSettings.branchId}
            screenName={selectedScreenForSettings.name}
          />
      )}

      {/* YENİ: PYK Ayarlar Modalı */}
      {selectedPykScreenForSettings && (
          <PykScreenSettingsModal 
            open={isPykSettingsModalOpen}
            onClose={handleClosePykSettingsModal}
            screenId={selectedPykScreenForSettings.id}
            companyId={companyId}
            screenName={selectedPykScreenForSettings.name}
          />
      )}

      {/* ✨ Genel Duyuru Sistemi Modali */}
      <AnnouncementsManagementModal
        open={generalAnnouncementsOpen}
        mode="general"
        onClose={() => setGeneralAnnouncementsOpen(false)}
      />

      {/* ✨ Ekran-Bazlı Duyuru Modali */}
      <AnnouncementsManagementModal
        open={!!perScreenAnnouncements}
        mode="per-screen"
        screenId={perScreenAnnouncements?.screenId ?? null}
        screenName={perScreenAnnouncements?.screenName ?? null}
        branchId={perScreenAnnouncements?.branchId ?? null}
        onClose={() => setPerScreenAnnouncements(null)}
      />
    </ScrollableContent>
  );
};

export default QuickSalePage; 